import { useState, useEffect, useMemo, useCallback } from 'react';
import './campuses-page.css';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import Pagination from '../../../components/v2/Pagination.jsx';
import CampusesTable, { CAMPUS_COLUMN_DEFS, platformLabel } from './components/CampusesTable.jsx';
import CampusDrawer from './components/CampusDrawer.jsx';
// Shared components from residents (fully generic)
import ActiveFilterBar from '../../../components/v2/ActiveFilterBar.jsx';
import Toast from '../../../components/v2/Toast.jsx';
import useToast from '../../../hooks/useToast.js';
import ColumnVisibilityToggle from '../../../components/v2/ColumnVisibilityToggle.jsx';
import TableSkeleton from '../../../components/v2/TableSkeleton.jsx';
// V2 data layer
import {
  getSession, getCampusId, getUserId, getSessionId, getRoleWeight,
} from '../../../services/session.js';
import {
  fetchCampuses, fetchCorporations, fetchTimezones, fetchUserRoles, addCampus,
} from './api/campusesApi.js';
import { exportCampusesCsv } from '../../../services/csvExport.js';
import usePersistedColumns from '../../../hooks/usePersistedColumns.js';
import usePersistedPerPage from '../../../hooks/usePersistedPerPage.js';
import {
  iAmHigherAuthority as iAmHigherAuth,
  reloadSessionCorp,
} from '../../../libs.jsx';
import ImpersonateDialog from '../../../components/v2/ImpersonateDialog.jsx';
import { impersonationRoles, startImpersonation } from '../../../utils/impersonation.js';

/** All column keys (used for validation when reading from localStorage) */
const ALL_COLUMN_KEYS = CAMPUS_COLUMN_DEFS.map((c) => c.key);

/** Default visible columns — show all by default (user can hide via column toggle) */
const DEFAULT_VISIBLE = ALL_COLUMN_KEYS;

/**
 * CampusesPage — V2 cross-campus admin overview.
 *
 * Read + Add, with checkboxes for bulk export, and Impersonate
 * in the per-row 3-dot kebab menu.
 */
const CampusesPage = () => {
  // ==========================================================================
  // SESSION
  // ==========================================================================
  const campusId = useMemo(() => getCampusId(), []);
  const myRoleWeight = useMemo(() => getRoleWeight(), []);
  const addRights = useMemo(() => iAmHigherAuth(80, myRoleWeight), [myRoleWeight]);

  /** Builds the session params object used by API calls */
  const getSessionParams = useCallback(() => ({
    campusId,
    userId: getUserId(),
    clientId: getSessionId(),
  }), [campusId]);

  // ==========================================================================
  // TOAST
  // ==========================================================================
  const { toast, showToast, dismissToast } = useToast(6000);

  // ==========================================================================
  // DATA STATE
  // ==========================================================================
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({});
  const [sortColumn, setSortColumn] = useState('campus_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = usePersistedPerPage();
  const [visibleColumns, handleColumnToggle] = usePersistedColumns('campuses', DEFAULT_VISIBLE, ALL_COLUMN_KEYS);
  const [selectedIds, setSelectedIds] = useState([]);

  // Drawer + support data
  const [corporations, setCorporations] = useState([]);
  const [timezones, setTimezones] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Impersonate
  const [impersonateTarget, setImpersonateTarget] = useState(null);
  const [impersonateRoleWeight, setImpersonateRoleWeight] = useState(myRoleWeight);
  const [impersonating, setImpersonating] = useState(false);

  // ==========================================================================
  // DATA LOADING
  // ==========================================================================
  const loadCampuses = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchCampuses(campusId, true);
      setData(result || []);
    } catch (err) {
      console.error('[CampusesPage] Load campuses error:', err);
      showToast('error', 'Failed to load campuses');
    } finally {
      setLoading(false);
    }
  }, [campusId, showToast]);

  const loadCorporations = useCallback(async () => {
    try {
      const result = await fetchCorporations(campusId);
      setCorporations(result || []);
    } catch (err) {
      console.error('[CampusesPage] Load corporations error:', err);
    }
  }, [campusId]);

  const loadTimezones = useCallback(async () => {
    try {
      const result = await fetchTimezones(campusId);
      setTimezones(result || []);
    } catch (err) {
      console.error('[CampusesPage] Load timezones error:', err);
    }
  }, [campusId]);

  const loadUserRoles = useCallback(async () => {
    try {
      const result = await fetchUserRoles(campusId);
      setUserRoles(result || []);
    } catch (err) {
      console.error('[CampusesPage] Load user roles error:', err);
    }
  }, [campusId]);

  useEffect(() => {
    loadCampuses();
    loadCorporations();
    loadTimezones();
    loadUserRoles();
  }, [loadCampuses, loadCorporations, loadTimezones, loadUserRoles]);

  // ==========================================================================
  // FILTER / SORT PIPELINE
  // ==========================================================================
  const BOOLEAN_COLUMNS = ['is_resident_checkin', 'is_rounding', 'offsite_active', 'udp_broadcast'];

  const filtered = useMemo(() => {
    let result = [...data];

    // 1. Text search
    if (search.trim()) {
      const s = search.toLowerCase();
      result = result.filter((r) =>
        r.campus_name?.toLowerCase().includes(s) ||
        r.corporate_name?.toLowerCase().includes(s) ||
        r.campus_street_address?.toLowerCase().includes(s) ||
        r.campus_city?.toLowerCase().includes(s) ||
        r.campus_state?.toLowerCase().includes(s) ||
        r.campus_zip?.toLowerCase().includes(s) ||
        r.campus_email?.toLowerCase().includes(s) ||
        r.campus_phone?.toLowerCase().includes(s)
      );
    }

    // 2. Column filters
    Object.entries(filters).forEach(([columnKey, values]) => {
      if (!values || values.length === 0) return;
      result = result.filter((row) => {
        /* Matched on the label, not on the raw tinyint: the option list offers
           "4.0"/"5.0", so comparing against row values of 0/1/null would never hit.
           Same helper builds both sides. */
        if (columnKey === 'is_cloud') {
          return values.includes(platformLabel(row.is_cloud));
        }
        if (columnKey === 'service_plan_active') {
          return values.some((v) => {
            if (v === 'Plan Active') return !!row.service_plan_active;
            if (v === 'No Plan') return !row.service_plan_active;
            return String(row.service_plan_active) === v;
          });
        }
        if (BOOLEAN_COLUMNS.includes(columnKey)) {
          return values.some((v) => {
            if (v === 'Yes') return !!row[columnKey];
            if (v === 'No') return !row[columnKey];
            return String(row[columnKey]) === v;
          });
        }
        const cellVal = row[columnKey];
        return values.includes(cellVal != null && cellVal !== '' ? String(cellVal) : '(empty)');
      });
    });

    // 3. Sort
    result.sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';

      /* Sort on the label so the order matches what is on screen. Sorting the raw
         tinyint would put NULL and 0 in separate groups even though both display as
         4.0, which reads as a broken sort. */
      if (sortColumn === 'is_cloud') {
        const cmp = platformLabel(aVal).localeCompare(platformLabel(bVal));
        return sortOrder === 'asc' ? cmp : -cmp;
      }
      // Boolean columns — sort by display label
      if (sortColumn === 'service_plan_active') {
        const aLabel = aVal ? 'Plan Active' : 'No Plan';
        const bLabel = bVal ? 'Plan Active' : 'No Plan';
        const cmp = aLabel.localeCompare(bLabel);
        return sortOrder === 'asc' ? cmp : -cmp;
      }
      if (BOOLEAN_COLUMNS.includes(sortColumn)) {
        const aLabel = aVal ? 'Yes' : 'No';
        const bLabel = bVal ? 'Yes' : 'No';
        const cmp = aLabel.localeCompare(bLabel);
        return sortOrder === 'asc' ? cmp : -cmp;
      }

      const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
      return sortOrder === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [data, search, filters, sortColumn, sortOrder]);

  // ==========================================================================
  // PAGINATION
  // ==========================================================================
  const totalFiltered = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / perPage));
  const safePage = Math.min(page, totalPages);

  const paginated = useMemo(() => {
    const start = (safePage - 1) * perPage;
    return filtered.slice(start, start + perPage);
  }, [filtered, safePage, perPage]);

  useEffect(() => { setPage(1); }, [search, filters, perPage]);

  /** Column labels for ActiveFilterBar */
  const columnLabels = useMemo(() => {
    const map = {};
    CAMPUS_COLUMN_DEFS.forEach((c) => { map[c.key] = c.label; });
    return map;
  }, []);

  // ==========================================================================
  // HANDLERS
  // ==========================================================================
  const handleSort = useCallback((key) => {
    setSortColumn((prev) => {
      if (prev === key) {
        setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortOrder('asc');
      return key;
    });
  }, []);

  const handleFilterChange = useCallback((columnKey, selectedValues) => {
    setFilters((prev) => ({ ...prev, [columnKey]: selectedValues }));
  }, []);

  const handleRemoveFilter = useCallback((columnKey) => {
    setFilters((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setFilters({});
  }, []);

  // handleColumnToggle provided by usePersistedColumns hook

  // Selection
  const handleSelectRow = useCallback((id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    const allIds = paginated.map((r) => r.campus_id);
    const allSelected = allIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !allIds.includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...allIds])]);
    }
  }, [paginated, selectedIds]);

  // Export
  const handleExportAll = useCallback(() => {
    if (filtered.length === 0) {
      showToast('error', 'No data to export');
      return;
    }
    exportCampusesCsv(filtered, 'campuses.csv');
    showToast('success', `Exported ${filtered.length} campuses`);
  }, [filtered, showToast]);

  const handleBulkExport = useCallback(() => {
    const selected = data.filter((d) => selectedIds.includes(d.campus_id));
    if (selected.length) {
      exportCampusesCsv(selected, 'campuses-selected.csv');
      showToast('success', `Exported ${selected.length} campuses`);
    }
  }, [selectedIds, data, showToast]);

  // Add Campus
  const handleAddCampus = useCallback(async (formData) => {
    const session = getSessionParams();
    const [success, errMsg] = await addCampus(formData, session);
    if (success) {
      showToast('success', `${formData.campus_name} has been added`);
      setDrawerOpen(false);

      // Always refresh the table data so the new campus appears
      loadCampuses();

      // If added to current corp, also reload session (picks up new campus in session)
      const dData = getSession();
      if (dData && dData.corp?.toString() === formData.corporate_id?.toString()) {
        reloadSessionCorp(formData.corporate_id, -1, dData.cognito, dData);
      }
    } else {
      showToast('error', `Failed to add campus: ${errMsg || 'Unknown error'}`);
    }
  }, [getSessionParams, showToast, loadCampuses]);

  // Impersonate
  const handleImpersonate = useCallback((campus) => {
    setImpersonateTarget(campus);
    setImpersonateRoleWeight(myRoleWeight);
    setImpersonating(false);
  }, [myRoleWeight]);

  const handleConfirmImpersonate = useCallback(() => {
    if (!impersonateTarget) return;
    const err = startImpersonation({
      corporateId: impersonateTarget.corporate_id,
      campusId: impersonateTarget.campus_id,
      roleWeight: impersonateRoleWeight,
      userRoles,
    });
    if (err) {
      showToast('error', err);
      return;
    }
    setImpersonating(true);
  }, [impersonateTarget, impersonateRoleWeight, userRoles, showToast]);

  /** Filtered role options for impersonate dialog */
  const filteredRoles = useMemo(() => impersonationRoles(userRoles, myRoleWeight), [userRoles, myRoleWeight]);

  // Bulk action bar
  const renderBulkBar = () => {
    if (selectedIds.length === 0) return null;
    return (
      <div className="dp-bulk-bar">
        <div className="dp-bulk-bar__left">
          <span className="dp-bulk-bar__count">
            {selectedIds.length} campus{selectedIds.length !== 1 ? 'es' : ''} selected
          </span>
          <button type="button" onClick={() => setSelectedIds([])} className="dp-bulk-bar__deselect">
            Deselect All
          </button>
        </div>
        <div className="dp-bulk-bar__right">
          <button type="button" onClick={handleBulkExport} className="dp-bulk-btn">
            <svg className="dp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
            Export
          </button>
        </div>
      </div>
    );
  };

  // ==========================================================================
  // RENDER
  // ==========================================================================
  return (
    <>
    <NavigationV2 />
    <div className="nav2-main">
    <div className="ps-page">
      {/* Page-level toast notification */}
      <Toast toast={toast} onDismiss={dismissToast} />

      {/* ============================================================
          PAGE HEADER
          ============================================================ */}
      <div className="ps-page-header">
        <div className="ps-page-header__inner">
          <div className="ps-breadcrumb-row">
            <div className="ps-breadcrumb">
              <span className="ps-breadcrumb__text">Customers</span>
              <svg className="dp-icon-md ps-breadcrumb__separator" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
              <span className="ps-breadcrumb__current">Campuses</span>
            </div>
          </div>
          <h1 className="ps-page-title">Campuses</h1>
        </div>
      </div>

      {/* ============================================================
          CONTENT
          ============================================================ */}
      <div className="dp-content">
        <div className="dp-controls">
          <div className="dp-controls__left">
            <div className="dp-search">
              <svg className="dp-icon-md dp-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
              <input
                type="search"
                placeholder="Search campuses..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="dp-search__input"
                aria-label="Search campuses"
              />
            </div>
          </div>
          <div className="dp-controls__right">
            <ColumnVisibilityToggle columns={CAMPUS_COLUMN_DEFS} visibleColumns={visibleColumns} onToggle={handleColumnToggle} />
            <button type="button" className="dp-btn dp-btn--secondary" onClick={handleExportAll} aria-label="Export campuses">
              <svg className="dp-icon-md" style={{ color: 'var(--color-text-secondary)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
              Export
            </button>
            {addRights ? (
              <button
                type="button"
                className="dp-btn dp-btn--primary"
                onClick={() => setDrawerOpen(true)}
              >
                <svg className="dp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                New Campus
              </button>
            ) : null}
          </div>
        </div>
        <ActiveFilterBar filters={filters} columnLabels={columnLabels} onRemoveFilter={handleRemoveFilter} onClearAll={handleClearAllFilters} />
        {renderBulkBar()}
        {loading ? (
          <TableSkeleton />
        ) : (
          <CampusesTable
            data={paginated}
            allData={data}
            visibleColumns={visibleColumns}
            sortColumn={sortColumn}
            sortOrder={sortOrder}
            onSort={handleSort}
            filters={filters}
            onFilterChange={handleFilterChange}
            selectedIds={selectedIds}
            onSelectRow={handleSelectRow}
            onSelectAll={handleSelectAll}
            onImpersonate={handleImpersonate}
          />
        )}
        {/* Pagination */}
        <Pagination page={safePage} totalPages={totalPages} total={totalFiltered} perPage={perPage} onPageChange={setPage} onPerPageChange={setPerPage} entityLabel="campuses" selectId="dp-cp-per-page" />
      </div>

      {/* Campus Drawer (Add / Migrate) */}
      <CampusDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSave={handleAddCampus}
        corporations={corporations}
        timezones={timezones}
      />

      {/* Impersonate Dialog */}
      {impersonateTarget ? (
        <ImpersonateDialog
          subject={impersonateTarget.campus_name}
          detail={impersonateTarget.corporate_name}
          roles={filteredRoles}
          myRoleWeight={myRoleWeight}
          roleWeight={impersonateRoleWeight}
          onRoleChange={setImpersonateRoleWeight}
          busy={impersonating}
          onCancel={() => setImpersonateTarget(null)}
          onConfirm={handleConfirmImpersonate}
        />
      ) : null}

    </div>
    </div>
    </>
  );
};

export default CampusesPage;
